/**
 * The handwriting recogniser: segmentation → per-character $P classification
 * → text → core parseAnswer. Pure TypeScript (no DOM), deterministic, so the
 * same code runs in the browser and in node tests.
 */
import { parseAnswer } from '@dropstack/core';
import type { HandwritingRecognizer, HwCharResult, HwChar, HwResult, HwStroke } from '../../contracts.ts';
import { type MatchTemplate, charConfidence, classifyGroup, makeTemplate } from './classify.ts';
import { type UserSample, parseSamples, serializeSamples, toUserSample } from './samples.ts';
import { type CharGroup, type Row, segment } from './segment.ts';
import { BUILTIN_TEMPLATES, type GlyphTemplate, flattenGlyph, isHwChar } from './templates.ts';

export interface RecognizerOptions {
  /** Built-in glyph templates (default: BUILTIN_TEMPLATES). */
  templates?: readonly GlyphTemplate[];
  /** Calibration samples kept per character; the oldest are dropped first. */
  maxSamplesPerChar?: number;
}

/** Scores for a detected stacked-fraction bar (it is a "/" by construction). */
const BAR_CANDIDATES = [
  { char: '/', score: 0.92 },
  { char: '-', score: 0.2 },
] as const;

interface StoredUserSample {
  sample: UserSample;
  template: MatchTemplate | null;
}

export class PointCloudRecognizer implements HandwritingRecognizer {
  private readonly builtIns: MatchTemplate[];
  private readonly maxPerChar: number;
  private samples: StoredUserSample[] = [];

  constructor(options: RecognizerOptions = {}) {
    this.maxPerChar = Math.max(1, options.maxSamplesPerChar ?? 6);
    this.builtIns = (options.templates ?? BUILTIN_TEMPLATES)
      .map((g) => makeTemplate(g.char, flattenGlyph(g), false))
      .filter((t) => t !== null);
  }

  recognize(strokes: HwStroke[]): HwResult {
    if (!strokes.some((s) => s.length > 0)) return { text: '', chars: [], confidence: 0, value: null };
    const templates = this.templates();
    const seg = segment(strokes);

    const charsOf = (row: Row, fallbackHeight: number | null): HwCharResult[] =>
      row.groups.map((g) => this.classifyChar(strokes, g, row.lineHeight ?? fallbackHeight, templates));

    let chars: HwCharResult[];
    if (seg.kind === 'row') {
      chars = charsOf(seg.row, null);
    } else {
      const h = seg.numerator.lineHeight ?? seg.denominator.lineHeight;
      const bar: HwCharResult = {
        candidates: BAR_CANDIDATES.map((c) => ({ ...c })),
        strokeIndices: seg.bar.strokeIndices,
        box: seg.bar.box,
      };
      chars = [
        ...charsOf(seg.before, h),
        ...charsOf(seg.numerator, h),
        bar,
        ...charsOf(seg.denominator, h),
        ...charsOf(seg.after, h),
      ];
    }

    const text = chars.map((c) => c.candidates[0]?.char ?? '').join('');
    const confidence = chars.length ? Math.min(...chars.map((c) => charConfidence(c.candidates))) : 0;
    return { text, chars, confidence, value: parseAnswer(text) };
  }

  addUserSample(char: HwChar, strokes: HwStroke[]): void {
    if (!isHwChar(char)) return;
    const sample = toUserSample(char, strokes);
    if (!sample) return;
    this.samples.push(this.store(sample));
    this.trim();
  }

  clearUserSamples(): void {
    this.samples = [];
  }

  userSampleCount(): number {
    return this.samples.length;
  }

  exportUserSamples(): string {
    return serializeSamples(this.samples.map((s) => s.sample));
  }

  /** Replaces the current samples. Throws on malformed JSON (see parseSamples). */
  importUserSamples(json: string): void {
    const parsed = parseSamples(json);
    this.samples = parsed.map((s) => this.store(s));
    this.trim();
  }

  private store(sample: UserSample): StoredUserSample {
    return { sample, template: makeTemplate(sample.char, sample.strokes, true) };
  }

  /** Keep only the newest `maxPerChar` samples of each character. */
  private trim(): void {
    const kept = new Map<HwChar, number>();
    const out: StoredUserSample[] = [];
    for (let i = this.samples.length - 1; i >= 0; i--) {
      const s = this.samples[i]!;
      const n = kept.get(s.sample.char) ?? 0;
      if (n >= this.maxPerChar) continue;
      kept.set(s.sample.char, n + 1);
      out.push(s);
    }
    this.samples = out.reverse();
  }

  private templates(): MatchTemplate[] {
    const user = this.samples.map((s) => s.template).filter((t) => t !== null);
    // User samples first: a close personal match tightens the early-abandon bound for everything after it.
    return [...user, ...this.builtIns];
  }

  private classifyChar(
    strokes: readonly HwStroke[],
    group: CharGroup,
    lineHeight: number | null,
    templates: readonly MatchTemplate[],
  ): HwCharResult {
    const own = group.strokeIndices.map((i) => strokes[i]!);
    return {
      candidates: classifyGroup(own, { lineHeight }, templates),
      strokeIndices: group.strokeIndices,
      box: group.box,
    };
  }
}
